// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Display formatting for the CRM customer Activity Log, extracted from
 * ActivityLogPanel (where it was an inline `formatActivityDate`).
 *
 * Distinct from the file-manager's `formatFileDate` (which is always an
 * absolute `MM/DD/YYYY, HH:MM`): activities are shown *relative* to now and
 * are future-aware, because the same feed renders both past events and
 * scheduled tasks/reminders. No money / reseller here — wall-neutral.
 */

/**
 * Humanise an activity timestamp relative to the current time:
 *
 *  • future  → `Tomorrow` (1 day), `In N days` (<7), else absolute date
 *  • today   → `Today at HH:MM`
 *  • 1 day   → `Yesterday at HH:MM`
 *  • <7 days → `N days ago`
 *  • else    → absolute `MM/DD/YYYY`
 *
 * "Days" are whole-day floors of the now−date delta, so the today/yesterday
 * boundary follows elapsed 24h windows (the prior inline behaviour, preserved
 * verbatim). Times use the en-US locale in the viewer's local timezone.
 */
export function formatActivityDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // Handle future dates (for tasks/reminders)
  if (diffDays < 0) {
    const daysUntil = Math.abs(diffDays)
    if (daysUntil === 1) {
      return `Tomorrow`
    } else if (daysUntil < 7) {
      return `In ${daysUntil} days`
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }
  }

  // Handle past/present dates
  if (diffDays === 0) {
    return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
  } else if (diffDays === 1) {
    return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }
}
